import { MenuOutlined } from '@ant-design/icons';
import { Menu } from 'antd';
import { MX_PAGE_VIEW_EVENT } from 'constants/mixpanelEvents';
import { useAuth } from 'hooks/useAuth';
import { useMe } from 'hooks/useMe';
import { useMixpanel } from 'hooks/useMixpanel';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCollapsed } from 'store/reducers/navigationDrawerReducer';
import { getUserFullname } from 'utils/auth';
import { filterPermittedLinks, pagesWithClientSelect } from './links';
import { NavigationWrapper } from './style';

function NavigationMenu() {
  const auth = useAuth();
  const user = useMe();
  const [openKeys, setOpenKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const { collapsed } = useSelector((state) => state.navigationDrawer);
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { trackEvent } = useMixpanel();
  const navigationLinks = useMemo(() => {
    return filterPermittedLinks(user);
  }, [user]);

  useEffect(() => {
    if (!auth) {
      navigate('/login');
    }
  }, [auth, navigate]);

  const { SubMenu } = Menu;

  useEffect(() => {
    if (pathname[pathname.length - 1] === '/') {
      setSelectedKeys([pathname.slice(0, pathname.length - 1)]);
    } else {
      setSelectedKeys([pathname]);
    }
  }, [pathname]);

  const openedSubmenuKey = useMemo(
    () => `/${pathname.split('/')[1]}`,
    [pathname]
  );

  useEffect(() => {
    if (openedSubmenuKey) {
      setOpenKeys([openedSubmenuKey]);
    }
  }, [openedSubmenuKey]);

  const rootSubmenuKeys = useMemo(() => {
    return navigationLinks
      .filter((link) => !!link.sublinks)
      .map((link) => link.rootPath);
  }, [navigationLinks]);

  const handleLinkClick = (path) => {
    const { text } = findCurrentLink(path);

    trackEvent(MX_PAGE_VIEW_EVENT, {
      page: text,
      user: getUserFullname(user),
    });
    handleNavigate(path);
  };

  const findCurrentLink = (path) => {
    for (let i = 0; i < navigationLinks.length; i += 1) {
      const link = navigationLinks[i];

      if (!link?.sublinks) {
        if (link.path === path) {
          return link;
        }
      } else {
        for (let j = 0; j < link.sublinks.length; j += 1) {
          if (link.sublinks[j].path === path) {
            return link.sublinks[j];
          }
        }
      }
    }
  };

  const handleNavigate = (path) => {
    const lastClientId = localStorage.getItem('last_client_id');

    if (lastClientId && pagesWithClientSelect.has(path)) {
      navigate(`${path}?client_id=${lastClientId}`);
    } else {
      navigate(path);
    }
  };

  const handleLinkOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);

    if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  return (
    <NavigationWrapper isCollapsed={collapsed}>
      <Menu
        mode="inline"
        theme="dark"
        inlineCollapsed={collapsed}
        openKeys={openKeys}
        selectedKeys={selectedKeys}
        onOpenChange={handleLinkOpenChange}
      >
        <Menu.Item
          key="menu_header"
          mode="vertical"
          title={false}
          style={{
            marginTop: '10.5px',
            paddingLeft: collapsed ? {} : 0,
            lineHeight: '19px',
            marginBottom: collapsed ? '8.5px' : '14.5px',
            cursor: 'default',
          }}
        >
          <div className="toggler-wrap">
            <img
              className="nav-logo"
              src="./assets/icons/logo/payda-sm.svg"
              alt="Payda logo"
            />

            <button
              className="nav-toggle-btn"
              onClick={() => dispatch(setCollapsed(!collapsed))}
            >
              <MenuOutlined
                style={{ fontSize: '19px', color: 'rgba(255, 255, 255, 0.65)' }}
              />
            </button>
          </div>
        </Menu.Item>
        {navigationLinks.map(({ text, path, icon, sublinks, rootPath }) => {
          if (!sublinks) {
            return (
              <Menu.Item
                key={path}
                icon={React.createElement(icon)}
                onClick={() => handleLinkClick(path)}
              >
                {text}
              </Menu.Item>
            );
          }

          return (
            <SubMenu
              key={rootPath}
              title={text}
              icon={React.createElement(icon)}
            >
              {sublinks?.length > 0 ? (
                <>
                  {sublinks.map(({ text, path, submenu }) =>
                    submenu?.length > 0 ? (
                      <SubMenu key={path} title={text}>
                        <>
                          {submenu.map(({ text, path }) => (
                            <Menu.Item
                              key={path}
                              onClick={() => handleNavigate(path)}
                            >
                              {text}
                            </Menu.Item>
                          ))}
                        </>
                      </SubMenu>
                    ) : (
                      <Menu.Item
                        key={path}
                        onClick={() => handleLinkClick(path)}
                      >
                        {text}
                      </Menu.Item>
                    )
                  )}
                </>
              ) : null}
            </SubMenu>
          );
        })}
      </Menu>
    </NavigationWrapper>
  );
}

export { NavigationMenu };
